import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { UploadCloud, X } from "lucide-react";
import styles from "../../../_css/AddRoleplay.module.css";
import { notifySuccess, notifyError, notifyInfo } from "../../helper/Toaster";

const UploadDocuments = ({
    handleNext,
    handleBack,
    handleUpload,
    step = 5,
    previousSelections = {},
    updateSelection,
}) => {
    const [image, setImage] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    // Load previous data if available
    useEffect(() => {
        const prev = previousSelections[`step${step}`];
        // Ensure we are loading a file object or handling it appropriately
        // Note: File objects cannot be easily persisted across full page reloads without re-fetching, 
        // but this works for in-memory wizard navigation.
        if (prev) {
            setImage(prev);
        }
    }, [previousSelections, step]);

    const handleFileChange = (file) => {
        if (file) {
            // Validating file type specifically here as well
            if (!file.type.match('image.*')) {
                notifyError("Please select a valid image file (PNG, JPG, JPEG)");
                return;
            }
            setImage(file);
        }
    };

    const handleRemoveImage = (e) => {
        e.preventDefault();
        setImage(null);
    };

    const onNext = async () => {
        try {
            //  Image exists - Upload it
            if (image) {
                const formData = new FormData();
                formData.append("files", image); // Using "files" as per your backend requirement

                // Call Parent API function
                setIsUploading(true);
                const response = await handleUpload(formData);
                console.log("Data: ", response)
                if (response?.success) {
                    notifySuccess(response?.data?.message || "Document uploaded successfully!");
                    updateSelection(step, image);
                    handleNext(true);
                } else {
                    notifyError(response?.data?.message || "Failed to upload document.");
                }
            }
            // CASE 2: No image - Skip (Optional)
            else {
                notifyInfo("Please Upload the Image")
                // updateSelection(step, null);
                // handleNext(true);
            }

        } catch (error) {
            console.error("Upload failed:", error);
            notifyError(error.message || "An unexpected error occurred.");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="step-card bg-white rounded-4 p-4 d-flex flex-column gap-4">
            {/* Header */}
            <div>
                <h4 className="fw-bold text-dark mb-1">Upload Image</h4>
                <p className="text-muted small mb-0">
                    Please upload your image in PNG, JPEG, or JPG format. Max 10MB.
                </p>
            </div>

            {/* Single Image Upload Area */}
            <div style={{ width: "100%" }}>
                <input
                    id="single-image-upload"
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    style={{ display: "none" }}
                    onChange={(e) => handleFileChange(e.target.files[0])}
                />

                <label
                    htmlFor="single-image-upload"
                    className="d-flex flex-column align-items-center justify-content-center p-4 border rounded-3 position-relative"
                    style={{
                        borderStyle: "dashed",
                        borderColor: "#E5E7EB",
                        backgroundColor: "#F9FAFB",
                        cursor: "pointer",
                        height: "250px",
                        width: "100%",
                        transition: "all 0.2s"
                    }}
                >
                    {image ? (
                        // PREVIEW STATE
                        <>
                            <div
                                onClick={handleRemoveImage}
                                className="position-absolute top-0 end-0 m-3 bg-white rounded-circle shadow-sm p-2 d-flex align-items-center justify-content-center"
                                style={{ width: "32px", height: "32px", zIndex: 10, cursor: "pointer" }}
                            >
                                <X size={18} color="red" />
                            </div>
                            <img
                                src={URL.createObjectURL(image)}
                                alt="Preview"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                    borderRadius: "8px"
                                }}
                            />
                            <p className="text-truncate mt-3 mb-0 fw-medium text-dark">
                                {image.name}
                            </p>
                        </>
                    ) : (
                        // EMPTY STATE
                        <>
                            <div className="bg-white p-3 rounded-circle shadow-sm mb-3">
                                <UploadCloud size={32} className="text-primary" />
                            </div>
                            <h6 className="text-dark fw-bold mb-1">Click to Upload</h6>
                            <span className="text-muted small">PNG, JPG, JPEG (Max 10MB)</span>
                        </>
                    )}
                </label>
            </div>

            {/* Navigation Buttons */}
            <div className="d-flex gap-3 mt-2">
                <Button variant="light" onClick={handleBack} className={styles.backButton}>
                    Back
                </Button>
                <Button
                    onClick={onNext}
                    className={styles.nextButton}
                    style={{ backgroundColor: "#000", color: "#fff" }}
                    disabled={isUploading}
                >
                    {isUploading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Uploading...
                        </>
                    ) : "Next"}
                </Button>
            </div>
        </div>
    );
};

export default UploadDocuments;